import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { listPasses, PASS_STATUS } from '../../api/studentPassesApi';
import { listUsers, chunkArray } from '../../api/adminUsersApi';
import { listOverrides } from '../../api/passLimitsApi';
import { useClassSections, type ClassSection } from '../../hooks/useClassSections';
import { getContextualError, logError } from '../../utils/errorHandling';
import { escapeCsv } from '../../utils/csvParsing';
import SearchFilter from '../common/SearchFilter';
import PaginationControls from '../common/PaginationControls';
import type { components } from '../../api/types.gen';

type PassResponse = components['schemas']['PassResponse'];
type UserResponse = components['schemas']['UserResponse'];

// A fetched pass augmented with the studentEmail resolved from the user roster
// (PassResponse itself has no such field — see loadHistoricalData below).
interface PassWithEmail extends PassResponse {
  studentEmail: string | null;
}

// Per-student aggregate built from the fetched passes: a per-period-label pass
// count plus the running total, keyed by lowercased studentEmail.
interface StudentStat {
  studentEmail: string;
  studentName: string;
  periods: Record<string, number>;
  totalPasses: number;
}

interface SortConfig {
  key: string;
  direction: 'asc' | 'desc';
}

interface PassHistoryViewProps {
  orgId?: number | string | null;
}

// Safety cap on the pagination loop below, mirroring ActivePassesPanel.js's
// TODAY_STATS_MAX_PAGES / ClassSectionsPanel.js's ALL_USERS_MAX_PAGES —
// guards against an infinite loop if the backend ever returns a
// non-advancing cursor. Page size is capped at 100 — listPassesQuery
// (apps/passes-api/src/schemas/passes.ts) rejects limit > 100 with a 400,
// the same cap ActivePassesPanel.js's fetchTodayStats already respects.
// Now that the selected date range is sent to the server (see
// getDateRangeBounds below), this cap should only ever be hit for the "All
// Time" view or a "Custom Range" with no start/end set, where the query is
// still unbounded — HISTORY_TRUNCATION_WARNING below covers that case.
const HISTORY_MAX_PAGES = 200;
const HISTORY_PAGE_SIZE = 100;

const HISTORY_TRUNCATION_WARNING = `Showing the most recent ${(
  HISTORY_MAX_PAGES * HISTORY_PAGE_SIZE
).toLocaleString()} passes; older history and CSV exports are incomplete for this school.`;

// GET /api/users' ids-lookup branch (apps/user-api/src/routes/user.ts)
// hard-caps ids at 100 and 400s above that — same cap
// adminUsersApi.js's createUsersBulk chunks at (BULK_CHUNK_SIZE = 100).
const ROSTER_CHUNK_SIZE = 100;

// Sort order for period columns/tabs (see `periods` in the component below):
// numeric "Period N" labels first in natural numeric order, then any other
// admin-defined periodName (e.g. "Advisory", "Lunch") alphabetically, then
// the component's synthesized "Unassigned"/"Unknown" labels last.
const NUMERIC_PERIOD_RE = /^Period (\d+)$/;
const SYNTHESIZED_LABEL_ORDER = ['Unassigned', 'Unknown'];
const periodLabelRank = (label: string): number => {
  if (NUMERIC_PERIOD_RE.test(label)) return 0;
  if (SYNTHESIZED_LABEL_ORDER.includes(label)) return 2;
  return 1;
};
const comparePeriodLabels = (a: string, b: string): number => {
  const rankA = periodLabelRank(a);
  const rankB = periodLabelRank(b);
  if (rankA !== rankB) return rankA - rankB;
  if (rankA === 0) {
    return (
      Number(a.match(NUMERIC_PERIOD_RE)?.[1]) -
      Number(b.match(NUMERIC_PERIOD_RE)?.[1])
    );
  }
  if (rankA === 2) {
    return (
      SYNTHESIZED_LABEL_ORDER.indexOf(a) - SYNTHESIZED_LABEL_ORDER.indexOf(b)
    );
  }
  // { numeric: true } orders trailing digits naturally (e.g. "Study Hall 2"
  // before "Study Hall 10") for admin-defined labels that aren't a plain
  // "Period N" -- same option TeacherDashboard.js already uses to sort
  // periodName (line 89).
  return a.localeCompare(b, undefined, { numeric: true });
};

const PassHistoryView: React.FC<PassHistoryViewProps> = ({ orgId }) => {
  const currentOrgId = orgId;
  const { userRole, currentUser } = useAuth();
  const { classSections } = useClassSections();
  const canViewUnlimitedOverrides =
    userRole === 'admin' || userRole === 'developer';

  // When a developer acts as another school, currentOrgId is that SELECTED
  // school — but the passes, roster, and class sections rendered below are all
  // still fetched for the CALLER'S OWN school: GET /passes (listPasses) and
  // GET /users (listUsers) are school-scoped to the auth token, and
  // useClassSections resolves off currentUser.schoolId — none of them follow
  // currentOrgId. The per-student "unlimited passes" overrides, by contrast,
  // ARE fetched for currentOrgId (listOverrides below). Applying the SELECTED
  // school's overrides to the OWN school's passes drops the wrong students
  // from the unlimited-passes filter and emits a wrong CSV "Unlimited Pass"
  // column. Mirror ActivePassesPanel.js's isViewingDifferentSchool guard
  // (lines ~64-66/206-212): warn, and don't apply the mismatched overrides.
  // Only treated as "different" once the caller's own schoolId is known —
  // otherwise the mismatch can't be detected and we fall back to the normal
  // same-school behavior.
  const isViewingDifferentSchool =
    currentOrgId != null &&
    currentUser?.schoolId != null &&
    String(currentOrgId) !== String(currentUser.schoolId);

  // Disabled-checkbox tooltip: admin gate first, then the cross-school case.
  let unlimitedCheckboxTitle: string | undefined;
  if (!canViewUnlimitedOverrides) {
    unlimitedCheckboxTitle =
      'Admin access required to view unlimited-pass status';
  } else if (isViewingDifferentSchool) {
    unlimitedCheckboxTitle =
      'Unavailable while viewing another school — pass history reflects your own school';
  }
  const [students, setStudents] = useState<StudentStat[]>([]);
  const [passes, setPasses] = useState<PassWithEmail[]>([]);
  const [teacherById, setTeacherById] = useState<Map<number, UserResponse>>(
    new Map()
  );
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [historyTruncated, setHistoryTruncated] = useState(false);
  const [unlimitedStudentIds, setUnlimitedStudentIds] = useState<Set<number>>(
    new Set()
  );
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: 'studentName',
    direction: 'asc',
  });
  const [currentPage, setCurrentPage] = useState(0); // Changed to 0-based indexing
  const [selectedStudent, setSelectedStudent] = useState<StudentStat | null>(
    null
  );
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [activeTab, setActiveTab] = useState('');
  const [dateRange, setDateRange] = useState('today');
  const [includeUnlimitedPasses, setIncludeUnlimitedPasses] = useState(false);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  const fetchGenerationRef = useRef<number>(0);

  const ITEMS_PER_PAGE = 50;

  // Period columns are derived from classSectionId -> classSection.periodName
  // (PassResponse has no currentPeriod field), the same resolution
  // ActivePassesPanel.js uses (lines 167-171).
  const periodNameByClassSectionId = useMemo(() => {
    const map = new Map<number, string>();
    classSections.forEach((cs) => map.set(cs.id, cs.periodName));
    return map;
  }, [classSections]);

  const getPeriodLabel = (classSectionId: number | null): string => {
    if (classSectionId == null) return 'Unassigned';
    return periodNameByClassSectionId.get(classSectionId) || 'Unknown';
  };

  // Origin-teacher names (getOriginTeacherName below) depend on classSections,
  // which loads asynchronously and starts empty (useClassSections). This
  // effect re-resolves teacher records whenever classSections changes, so
  // names populate once class sections finish loading -- independent of the
  // passes/roster fetch above, since TEACHER-role users (for whom the
  // 'Include Unlimited Passes' checkbox is disabled) have no other way to
  // trigger a refetch.
  useEffect(() => {
    const teacherIds = [
      ...new Set(classSections.map((cs) => cs.teacherId).filter(Boolean)),
    ];
    const missingIds = teacherIds.filter((id) => !teacherById.has(id));
    if (missingIds.length === 0) return;

    const idChunks = chunkArray(missingIds, ROSTER_CHUNK_SIZE);
    Promise.all(
      idChunks.map((idChunk) =>
        listUsers({ ids: idChunk, limit: idChunk.length })
      )
    )
      .then((chunkResults) => {
        const newTeachers = chunkResults.flatMap(({ data }) => data);
        setTeacherById((prev) => {
          const next = new Map(prev);
          newTeachers.forEach((u) => next.set(u.id, u));
          return next;
        });
      })
      .catch((error) =>
        logError(error, 'client_api', 'PassHistoryView:loadTeachers')
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classSections]);

  // PassResponse has no sourceClassroom field — resolve the origin teacher
  // via classSectionId -> classSection.teacherId -> teacherById (populated by
  // the effect above).
  const classSectionById = new Map<number, ClassSection>(
    classSections.map((cs): [number, ClassSection] => [cs.id, cs])
  );
  const getOriginTeacherName = (pass: PassResponse): string => {
    const cs = pass.classSectionId
      ? classSectionById.get(pass.classSectionId)
      : null;
    return (cs && teacherById.get(cs.teacherId)?.name) || 'Unknown';
  };

  // Clears out results from a previously-selected range when the effect
  // below skips fetching, so stale data doesn't linger under the "custom"
  // label (e.g. no dates set yet, or an inverted start > end range).
  const clearStaleResults = (): void => {
    setPasses([]);
    setStudents([]);
    setHistoryTruncated(false);
    setLoading(false);
    setLoadError(null);
  };

  const startFetch = (): void => {
    const generation = ++fetchGenerationRef.current;
    loadHistoricalData(() => fetchGenerationRef.current !== generation);
  };

  // Refetches whenever the selected date range changes (in addition to org
  // change) since the range is now sent to the server as from/to bounds —
  // see getDateRangeBounds/loadHistoricalData below.
  useEffect(() => {
    if (!currentOrgId) return;
    if (dateRange === 'custom') {
      if (!customStartDate && !customEndDate) {
        clearStaleResults();
        return;
      }
      if (customStartDate && customEndDate && customStartDate > customEndDate) {
        clearStaleResults();
        return;
      }
    }

    startFetch();

    return () => {
      fetchGenerationRef.current += 1;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrgId, dateRange, customStartDate, customEndDate]);

  // Load current per-student "unlimited passes" overrides. Gated to
  // ADMIN/SUPER_ADMIN — GET /schools/:schoolId/pass-limits 403s for TEACHER,
  // and this component is also rendered from TeacherDashboard.js.
  //
  // Guards against a stale response clobbering `unlimitedStudentIds` for the
  // wrong currentOrgId: if currentOrgId changes while a fetch is in flight,
  // the cleanup below flips `cancelled` so an earlier request that resolves
  // after a later one is dropped instead of overwriting state. Mirrors the
  // cancelled-flag pattern in AdminPanel.js's checkSchoolSetupStatus effect.
  useEffect(() => {
    // Skip in developer/cross-school mode: the passes shown are the caller's
    // OWN school's, so the selected school's overrides don't apply. Clear any
    // previously-loaded set rather than misapplying it to the filter/CSV (see
    // isViewingDifferentSchool above).
    if (isViewingDifferentSchool) {
      setUnlimitedStudentIds(new Set());
      return;
    }
    if (!canViewUnlimitedOverrides || !currentOrgId) return;

    let cancelled = false;
    listOverrides(currentOrgId)
      .then(({ data }) => {
        if (!cancelled) {
          setUnlimitedStudentIds(
            new Set(data.filter((o) => o.unlimited).map((o) => o.studentId))
          );
        }
      })
      .catch((error) =>
        logError(error, 'client_api', 'PassHistoryView:loadOverrides')
      );

    return () => {
      cancelled = true;
    };
  }, [canViewUnlimitedOverrides, currentOrgId, isViewingDifferentSchool]);

  // Reset page when a filter that can shrink the result set changes.
  // Toggling 'Include Unlimited Passes' off can drop the list below the
  // current page's range; without this reset, paginatedStudents slices to
  // empty and PaginationControls (which renders null when there's only one
  // page) disappears, stranding the viewer on a blank page.
  useEffect(() => {
    setCurrentPage(0);
  }, [
    searchTerm,
    dateRange,
    customStartDate,
    customEndDate,
    includeUnlimitedPasses,
  ]);

  // Converts the selected date range into from/to bounds for the server-side
  // filter (listPassesQuery's from/to — apps/passes-api/src/schemas/passes.ts,
  // compared against requestedAt), mirroring isPassInDateRange's per-range
  // semantics below so the fetched set matches what was previously computed
  // client-side. 'all' is intentionally left unbounded (falls back to paging
  // the full history, capped by HISTORY_MAX_PAGES). 'custom' with neither
  // date set never reaches this fetch path -- the effect above short-circuits
  // and clears results before loadHistoricalData is called; the unbounded {}
  // this function returns for that case is only ever consumed by
  // isPassInDateRange for client-side filtering of an already-empty `passes`
  // array.
  const getDateRangeBounds = (range: string): { from?: string; to?: string } => {
    switch (range) {
      case 'today': {
        const from = new Date();
        from.setHours(0, 0, 0, 0);
        const to = new Date();
        to.setHours(23, 59, 59, 999);
        return { from: from.toISOString(), to: to.toISOString() };
      }
      case '7days': {
        const from = new Date();
        from.setDate(from.getDate() - 7);
        from.setHours(0, 0, 0, 0);
        return { from: from.toISOString() };
      }
      case '30days': {
        const from = new Date();
        from.setDate(from.getDate() - 30);
        from.setHours(0, 0, 0, 0);
        return { from: from.toISOString() };
      }
      case 'custom': {
        const bounds: { from?: string; to?: string } = {};
        if (customStartDate) {
          const from = new Date(customStartDate);
          from.setHours(0, 0, 0, 0);
          bounds.from = from.toISOString();
        }
        if (customEndDate) {
          const to = new Date(customEndDate);
          to.setHours(23, 59, 59, 999);
          bounds.to = to.toISOString();
        }
        return bounds;
      }
      default:
        return {};
    }
  };

  const loadHistoricalData = async (
    isCancelled: () => boolean = () => false
  ): Promise<void> => {
    if (!currentOrgId) return;

    setLoading(true);
    setLoadError(null);
    setHistoryTruncated(false);
    try {
      // GET /passes (apps/passes-api/src/routes/passes.ts) is school-scoped
      // for staff roles and returns every pass in the school — the selected
      // date range is now sent as from/to so only relevant rows are fetched,
      // rather than paging through unrelated history first and filtering
      // client-side. Still cursor-paged at HISTORY_PAGE_SIZE (the backend's
      // max) since even a bounded range can exceed one page.
      const { from, to } = getDateRangeBounds(dateRange);
      let allPasses: PassResponse[] = [];
      let cursor: string | undefined;
      let truncated = false;
      for (let i = 0; i < HISTORY_MAX_PAGES; i++) {
        const page = await listPasses({
          status: PASS_STATUS.COMPLETED,
          from,
          to,
          cursor,
          limit: HISTORY_PAGE_SIZE,
        });
        allPasses = allPasses.concat(page.data);
        if (!page.nextCursor) break;
        cursor = page.nextCursor;
        if (i === HISTORY_MAX_PAGES - 1) {
          // Loop is about to exit at the cap while more pages remain —
          // surface this rather than silently dropping older history.
          truncated = true;
        }
      }
      if (isCancelled()) return;
      setHistoryTruncated(truncated);

      // Resolve studentEmail (PassResponse has no such field) via the user
      // roster, chunked at ROSTER_CHUNK_SIZE per GET /api/users' ids-lookup
      // cap.
      const studentIds = [...new Set(allPasses.map((p) => p.studentId))];
      let newUserById = new Map<number, UserResponse>();
      if (studentIds.length > 0) {
        const idChunks = chunkArray(studentIds, ROSTER_CHUNK_SIZE);
        const chunkResults = await Promise.all(
          idChunks.map((idChunk) =>
            listUsers({ ids: idChunk, limit: idChunk.length })
          )
        );
        if (isCancelled()) return;
        const allUsers = chunkResults.flatMap(({ data }) => data);
        newUserById = new Map(allUsers.map((u) => [u.id, u]));
      }

      const passesWithEmail: PassWithEmail[] = allPasses.map((p) => ({
        ...p,
        studentEmail: newUserById.get(p.studentId)?.email || null,
      }));

      // Group passes by student and period
      const studentStats = new Map<string, StudentStat>();

      passesWithEmail.forEach((pass) => {
        if (!pass.studentEmail) return;

        const email = pass.studentEmail.toLowerCase();
        const period = getPeriodLabel(pass.classSectionId);

        let student = studentStats.get(email);
        if (!student) {
          const studentName = pass.studentName || 'Unknown Student';

          student = {
            studentEmail: email,
            studentName: studentName,
            periods: {},
            totalPasses: 0,
          };
          studentStats.set(email, student);
        }

        if (!student.periods[period]) {
          student.periods[period] = 0;
        }
        student.periods[period]++;
        student.totalPasses++;
      });

      const studentsArray = Array.from(studentStats.values());

      if (isCancelled()) return;
      setStudents(studentsArray);
      setPasses(passesWithEmail);
    } catch (error) {
      if (isCancelled()) return;
      logError(error, 'client_api', 'PassHistoryView:loadHistoricalData');
      setLoadError(
        getContextualError(
          error,
          undefined,
          'Failed to load pass history. Please try again.'
        )
      );
    } finally {
      if (!isCancelled()) {
        setLoading(false);
      }
    }
  };

  // Recalculate student stats when date range or the unlimited-passes filter
  // changes
  useEffect(() => {
    if (passes.length === 0) {
      setStudents([]);
      return;
    }
    // Filter passes by date range and (unless included) drop students
    // with a *current* "unlimited passes" override — present-day flag,
    // not historical (see the KNOWN GAP note above handleExportCSV's
    // headers for the same limitation on the CSV export).
    const filteredPasses = passes.filter(
      (pass) =>
        isPassInDateRange(pass, dateRange) &&
        // In cross-school mode unlimitedStudentIds is cleared and must not
        // drop anyone — the override set belongs to a different school than
        // these passes (see isViewingDifferentSchool above).
        (includeUnlimitedPasses ||
          isViewingDifferentSchool ||
          !unlimitedStudentIds.has(pass.studentId))
    );

    // Recalculate student stats with filtered passes
    const studentStats = new Map<string, StudentStat>();

    filteredPasses.forEach((pass) => {
      if (!pass.studentEmail) return;

      const email = pass.studentEmail.toLowerCase();
      const period = getPeriodLabel(pass.classSectionId);

      let student = studentStats.get(email);
      if (!student) {
        student = {
          studentEmail: email,
          studentName: pass.studentName || 'Unknown Student',
          periods: {},
          totalPasses: 0,
        };
        studentStats.set(email, student);
      }

      if (!student.periods[period]) {
        student.periods[period] = 0;
      }
      student.periods[period]++;
      student.totalPasses++;
    });

    const studentsArray = Array.from(studentStats.values());
    setStudents(studentsArray);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    passes,
    dateRange,
    customStartDate,
    customEndDate,
    includeUnlimitedPasses,
    unlimitedStudentIds,
    isViewingDifferentSchool,
    periodNameByClassSectionId,
  ]);

  const isPassInDateRange = (pass: PassResponse, range: string): boolean => {
    if (range === 'all') return true;

    // Parse the date from the REST response (ISO-8601 string)
    let passDate: Date;
    try {
      passDate = new Date(pass.requestedAt);
    } catch (error) {
      console.warn('Error parsing pass date:', pass.requestedAt, error);
      return false;
    }

    // Validate date
    if (isNaN(passDate.getTime())) {
      console.warn('Invalid date found in pass:', pass.requestedAt);
      return false;
    }

    const { from, to } = getDateRangeBounds(range);
    if (from && passDate < new Date(from)) return false;
    if (to && passDate > new Date(to)) return false;
    return true;
  };

  // Column/tab set, derived from whatever period labels actually show up in
  // `students` (populated via getPeriodLabel above) rather than a hardcoded
  // list — admins can set classSection.periodName freely (SchoolPeriodsPanel.js),
  // and the component's own synthesized "Unassigned"/"Unknown" labels aren't
  // in any fixed set either, so any of those could otherwise land in
  // totalPasses but appear in no column/tab. Ordered via comparePeriodLabels
  // below: numeric "Period N" labels first in natural order, then other
  // admin-defined labels alphabetically, then "Unassigned"/"Unknown" last.
  const periods = useMemo<string[]>(() => {
    const labelSet = new Set<string>();
    students.forEach((student) => {
      Object.keys(student.periods).forEach((label) => labelSet.add(label));
    });
    return Array.from(labelSet).sort(comparePeriodLabels);
  }, [students]);

  // `periods` is now live-derived from `students` (unlike the old hardcoded
  // PERIODS list, which was always a superset of any student's periods) --
  // if it changes while the detail modal is already open (e.g. the
  // unlimitedStudentIds/date-range recompute effects above finish after the
  // modal was opened) and the active tab's label no longer exists, fall back
  // to the first available tab instead of leaving no tab marked active.
  useEffect(() => {
    if (showDetailModal && activeTab && !periods.includes(activeTab)) {
      setActiveTab(periods[0] || '');
    }
  }, [periods, showDetailModal, activeTab]);

  const handleSort = (key: string): void => {
    const direction =
      sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    setSortConfig({ key, direction });
  };

  const getSortedStudents = (): StudentStat[] => {
    const filtered = students.filter(
      (student) =>
        student.studentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.studentEmail.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return filtered.sort((a, b) => {
      let aValue: string | number;
      let bValue: string | number;

      if (
        sortConfig.key === 'studentName' ||
        sortConfig.key === 'studentEmail'
      ) {
        aValue = a[sortConfig.key];
        bValue = b[sortConfig.key];
      } else if (sortConfig.key === 'totalPasses') {
        aValue = a.totalPasses;
        bValue = b.totalPasses;
      } else {
        // Period columns
        aValue = a.periods[sortConfig.key] || 0;
        bValue = b.periods[sortConfig.key] || 0;
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (sortConfig.direction === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });
  };

  const openDetailModal = (student: StudentStat): void => {
    setSelectedStudent(student);
    setActiveTab(periods[0] || '');
    setShowDetailModal(true);
  };

  const getPassesForStudentPeriod = (
    studentEmail: string,
    period: string
  ): PassWithEmail[] => {
    return passes
      .filter(
        (pass) =>
          pass.studentEmail?.toLowerCase() === studentEmail.toLowerCase() &&
          getPeriodLabel(pass.classSectionId) === period &&
          isPassInDateRange(pass, dateRange)
        // No status filter needed - listPasses is already scoped to COMPLETED passes
      )
      .sort(
        (a, b) =>
          new Date(b.requestedAt).getTime() - new Date(a.requestedAt).getTime()
      );
  };

  const formatDuration = (pass: PassResponse): string => {
    if (!pass.activatedAt || !pass.returnedAt) return '--';

    const activated = new Date(pass.activatedAt);
    const returned = new Date(pass.returnedAt);

    const durationMs = returned.getTime() - activated.getTime();
    const minutes = Math.floor(durationMs / (1000 * 60));

    return minutes < 1 ? '--' : `${minutes} min`;
  };

  const handleExportCSV = (): void => {
    setIsExporting(true);

    try {
      if (historyTruncated) {
        alert(HISTORY_TRUNCATION_WARNING);
      }

      // Filter passes by current date range
      const filteredPasses = passes.filter((pass) =>
        isPassInDateRange(pass, dateRange)
      );

      if (filteredPasses.length === 0) {
        alert('No passes found in the selected date range.');
        setIsExporting(false);
        return;
      }

      // KNOWN GAP: the legacy CSV export also had 'Subcategory' and 'Reason'
      // columns. hallpass-backend's Destination model has no subcategory
      // field and Pass has no field distinct from `note` for a request
      // reason — both were Firestore-era-only and have no backend
      // equivalent, so they are dropped here rather than fabricated.
      const headers = [
        'Date',
        'Student Name',
        'Student Email',
        'Period',
        'Origin (Teacher)',
        'Destination',
        'Duration (minutes)',
        'Status',
        'Created At',
        'Approved At',
        'Approved By',
        'Activated At',
        'Returned At',
        'Note',
        'Unlimited Pass',
      ];

      // Helper to format dates
      const formatDate = (dateStr: string | null): string => {
        if (!dateStr) return '';
        try {
          const date = new Date(dateStr);
          return date.toLocaleString('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: true,
          });
        } catch {
          return '';
        }
      };

      const formatDateOnly = (dateStr: string | null): string => {
        if (!dateStr) return '';
        try {
          const date = new Date(dateStr);
          return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
          });
        } catch {
          return '';
        }
      };

      // CSV Rows
      const rows = filteredPasses.map((pass) => {
        // Calculate duration in minutes
        let durationMinutes: string | number = '';
        if (pass.activatedAt && pass.returnedAt) {
          const activated = new Date(pass.activatedAt);
          const returned = new Date(pass.returnedAt);
          const durationMs = returned.getTime() - activated.getTime();
          durationMinutes = Math.floor(durationMs / (1000 * 60));
          if (durationMinutes < 1) durationMinutes = '';
        }

        // Blank (not 'No') in cross-school mode: unlimitedStudentIds belongs
        // to a different school than these passes, so its unlimited status is
        // unknown here — emitting a definitive 'No' would be as wrong as the
        // mismatched 'Yes' this guard prevents (see isViewingDifferentSchool).
        let unlimitedLabel: string = '';
        if (!isViewingDifferentSchool) {
          unlimitedLabel = unlimitedStudentIds.has(pass.studentId)
            ? 'Yes'
            : 'No';
        }

        return [
          formatDateOnly(pass.requestedAt),
          escapeCsv(pass.studentName || ''),
          escapeCsv(pass.studentEmail || ''),
          escapeCsv(getPeriodLabel(pass.classSectionId)),
          escapeCsv(getOriginTeacherName(pass)),
          escapeCsv(pass.destinationName || ''),
          durationMinutes,
          escapeCsv(pass.status || ''),
          formatDate(pass.requestedAt),
          formatDate(pass.approvedAt),
          escapeCsv(pass.approverName || ''),
          formatDate(pass.activatedAt),
          formatDate(pass.returnedAt),
          escapeCsv(pass.note || ''),
          unlimitedLabel,
        ].join(',');
      });

      // Combine header and rows
      const csvContent = [headers.join(','), ...rows].join('\n');

      // Generate filename with date range
      let filename: string = 'pass_history';
      if (dateRange === 'custom' && customStartDate && customEndDate) {
        filename += `_${customStartDate}_to_${customEndDate}`;
      } else if (dateRange === 'custom' && customStartDate) {
        filename += `_from_${customStartDate}`;
      } else if (dateRange === 'custom' && customEndDate) {
        filename += `_until_${customEndDate}`;
      } else {
        filename += `_${dateRange}`;
      }
      filename += `_${new Date().toISOString().split('T')[0]}.csv`;

      // Download CSV
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting CSV:', error);
      alert('Error exporting CSV. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const sortedStudents = getSortedStudents();
  const totalPages = Math.ceil(sortedStudents.length / ITEMS_PER_PAGE);
  const paginatedStudents = sortedStudents.slice(
    currentPage * ITEMS_PER_PAGE, // Fixed: use 0-based indexing directly
    (currentPage + 1) * ITEMS_PER_PAGE
  );

  if (loading) {
    return <div style={{ padding: '20px' }}>Loading historical data...</div>;
  }

  if (loadError) {
    return (
      <div style={{ padding: '20px' }}>
        <p style={{ color: '#dc3545', fontWeight: 'bold' }}>{loadError}</p>
        <button
          onClick={startFetch}
          style={{
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            padding: '8px 16px',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      {isViewingDifferentSchool && (
        <div
          style={{
            backgroundColor: '#fff3cd',
            color: '#856404',
            border: '1px solid #ffeeba',
            borderRadius: '4px',
            padding: '10px 16px',
            marginBottom: '16px',
            fontWeight: 'bold',
          }}
        >
          ⚠️ You're viewing another school, but the pass history, class-section
          names, and unlimited-pass status below still reflect your own school —
          they cannot yet follow the school switch. The unlimited-passes filter
          and CSV "Unlimited Pass" column are disabled here to avoid mixing the
          two schools' data.
        </div>
      )}
      {historyTruncated && (
        <div
          style={{
            backgroundColor: '#fff3cd',
            color: '#856404',
            border: '1px solid #ffeeba',
            borderRadius: '4px',
            padding: '10px 16px',
            marginBottom: '16px',
            fontWeight: 'bold',
          }}
        >
          {HISTORY_TRUNCATION_WARNING}
        </div>
      )}
      <div
        style={{
          display: 'flex',
          gap: '20px',
          alignItems: 'center',
          marginBottom: '20px',
          flexWrap: 'wrap',
        }}
      >
        <div>
          <label
            htmlFor="date-range-select"
            style={{ marginRight: '8px', fontWeight: 'bold' }}
          >
            Date Range:
          </label>
          <select
            id="date-range-select"
            value={dateRange}
            onChange={(e) => {
              setDateRange(e.target.value);
              if (e.target.value !== 'custom') {
                setCustomStartDate('');
                setCustomEndDate('');
              }
            }}
            style={{
              padding: '8px 12px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              fontSize: '14px',
            }}
          >
            <option value="today">Today</option>
            <option value="7days">Last 7 Days</option>
            <option value="30days">Last 30 Days</option>
            <option value="all">All Time</option>
            <option value="custom">Custom Range</option>
          </select>
        </div>
        {dateRange === 'custom' && (
          <>
            <div>
              <label
                htmlFor="custom-start-date"
                style={{ marginRight: '8px', fontWeight: 'bold' }}
              >
                Start:
              </label>
              <input
                id="custom-start-date"
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                style={{
                  padding: '8px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                }}
              />
            </div>
            <div>
              <label
                htmlFor="custom-end-date"
                style={{ marginRight: '8px', fontWeight: 'bold' }}
              >
                End:
              </label>
              <input
                id="custom-end-date"
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                style={{
                  padding: '8px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                }}
              />
            </div>
          </>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <input
            type="checkbox"
            id="include-unlimited-passes"
            checked={includeUnlimitedPasses}
            disabled={!canViewUnlimitedOverrides || isViewingDifferentSchool}
            title={unlimitedCheckboxTitle}
            onChange={(e) => setIncludeUnlimitedPasses(e.target.checked)}
            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
          />
          <label
            htmlFor="include-unlimited-passes"
            style={{
              fontWeight: 'bold',
              cursor: 'pointer',
              userSelect: 'none',
            }}
          >
            Include Unlimited Passes
          </label>
        </div>
        <SearchFilter
          value={searchTerm} // Fixed: use correct prop name
          onChange={setSearchTerm} // Fixed: use correct prop name
          placeholder="Search by student name or email..."
        />
        <button
          onClick={handleExportCSV}
          disabled={isExporting || passes.length === 0}
          style={{
            backgroundColor: '#28a745',
            color: 'white',
            border: 'none',
            padding: '8px 16px',
            borderRadius: '4px',
            cursor:
              passes.length === 0 || isExporting ? 'not-allowed' : 'pointer',
            fontSize: '14px',
            fontWeight: 'bold',
            opacity: passes.length === 0 || isExporting ? 0.6 : 1,
          }}
        >
          {isExporting ? 'Exporting...' : 'Export CSV'}
        </button>
      </div>

      <div style={{ overflowX: 'auto', marginTop: '20px' }}>
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '14px',
          }}
        >
          <thead>
            <tr style={{ backgroundColor: '#f8f9fa' }}>
              <th
                onClick={() => handleSort('studentEmail')}
                style={headerStyle}
              >
                Student Email {getSortIcon('studentEmail')}
              </th>
              <th onClick={() => handleSort('studentName')} style={headerStyle}>
                Student Name {getSortIcon('studentName')}
              </th>
              {periods.map((period) => (
                <th
                  key={period}
                  onClick={() => handleSort(period)}
                  style={headerStyle}
                >
                  {period.replace('Period ', 'P')} {getSortIcon(period)}
                </th>
              ))}
              <th onClick={() => handleSort('totalPasses')} style={headerStyle}>
                Total {getSortIcon('totalPasses')}
              </th>
              <th style={headerStyle}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedStudents.map((student, index) => (
              <tr
                key={student.studentEmail}
                style={{
                  backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8f9fa',
                  borderBottom: '1px solid #dee2e6',
                }}
              >
                <td style={cellStyle}>{student.studentEmail}</td>
                <td style={cellStyle}>{student.studentName}</td>
                {periods.map((period) => (
                  <td
                    key={period}
                    style={{ ...cellStyle, textAlign: 'center' }}
                  >
                    {student.periods[period] || 0}
                  </td>
                ))}
                <td
                  style={{
                    ...cellStyle,
                    textAlign: 'center',
                    fontWeight: 'bold',
                  }}
                >
                  {student.totalPasses}
                </td>
                <td style={cellStyle}>
                  <button
                    onClick={() => openDetailModal(student)}
                    style={{
                      backgroundColor: '#007bff',
                      color: 'white',
                      border: 'none',
                      padding: '4px 8px',
                      borderRadius: '3px',
                      cursor: 'pointer',
                      fontSize: '12px',
                    }}
                  >
                    View
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <PaginationControls
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        totalItems={sortedStudents.length}
        pageSize={ITEMS_PER_PAGE} // Fixed: use correct prop name for PaginationControls
      />

      {/* Detail Modal */}
      {showDetailModal && selectedStudent && (
        <div
          style={modalOverlayStyle}
          onClick={() => setShowDetailModal(false)}
        >
          <div style={modalContentStyle} onClick={(e) => e.stopPropagation()}>
            <div style={modalHeaderStyle}>
              <h4 style={{ margin: 0 }}>
                Pass History: {selectedStudent.studentName}
              </h4>
              <button
                type="button"
                className="text-muted"
                onClick={() => setShowDetailModal(false)}
                style={closeButtonStyle}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            {/* Tab Navigation */}
            <div style={tabContainerStyle}>
              {periods.map((period) => {
                const count = selectedStudent.periods[period] || 0;
                return (
                  <button
                    key={period}
                    onClick={() => setActiveTab(period)}
                    style={{
                      ...tabButtonStyle,
                      backgroundColor:
                        activeTab === period ? '#007bff' : '#f8f9fa',
                      color: activeTab === period ? 'white' : '#495057',
                    }}
                  >
                    {period.replace('Period ', 'P')} ({count})
                  </button>
                );
              })}
            </div>

            {/* Tab Content */}
            <div style={tabContentStyle}>
              <h5 style={{ marginTop: 0 }}>
                {activeTab} - {selectedStudent.periods[activeTab] || 0} passes
              </h5>

              {getPassesForStudentPeriod(
                selectedStudent.studentEmail,
                activeTab
              ).length === 0 ? (
                <p className="text-muted" style={{ fontStyle: 'italic' }}>
                  No passes found for this period
                </p>
              ) : (
                <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  <table style={{ width: '100%', fontSize: '13px' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f8f9fa' }}>
                        <th style={detailHeaderStyle}>Date</th>
                        <th style={detailHeaderStyle}>Origin</th>
                        <th style={detailHeaderStyle}>Destination</th>
                        <th style={detailHeaderStyle}>Duration</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getPassesForStudentPeriod(
                        selectedStudent.studentEmail,
                        activeTab
                      ).map((pass) => (
                        <tr key={pass.id}>
                          <td style={detailCellStyle}>
                            {new Date(pass.requestedAt).toLocaleDateString(
                              'en-US',
                              {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                              }
                            )}
                          </td>
                          <td style={detailCellStyle}>
                            {getOriginTeacherName(pass)}
                          </td>
                          <td style={detailCellStyle}>
                            {pass.destinationName}
                          </td>
                          <td style={detailCellStyle}>
                            {formatDuration(pass)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  function getSortIcon(columnKey: string): string {
    if (sortConfig.key !== columnKey) return '↕️';
    return sortConfig.direction === 'asc' ? '↗️' : '↘️';
  }
};

// Styles
const headerStyle: React.CSSProperties = {
  padding: '12px 8px',
  textAlign: 'left',
  borderBottom: '2px solid #dee2e6',
  cursor: 'pointer',
  userSelect: 'none',
  fontSize: '13px',
  fontWeight: 'bold',
};

const cellStyle: React.CSSProperties = {
  padding: '8px',
  borderBottom: '1px solid #dee2e6',
};

const modalOverlayStyle: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
};

const modalContentStyle: React.CSSProperties = {
  backgroundColor: 'white',
  borderRadius: '8px',
  width: '90%',
  maxWidth: '800px',
  maxHeight: '80%',
  overflow: 'hidden',
  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
};

const modalHeaderStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '20px',
  borderBottom: '1px solid #dee2e6',
  backgroundColor: '#f8f9fa',
};

const closeButtonStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  fontSize: '24px',
  cursor: 'pointer',
};

const tabContainerStyle: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  padding: '10px 20px 0',
  borderBottom: '1px solid #dee2e6',
};

const tabButtonStyle: React.CSSProperties = {
  padding: '8px 12px',
  border: '1px solid #dee2e6',
  borderBottom: 'none',
  borderRadius: '4px 4px 0 0',
  cursor: 'pointer',
  fontSize: '12px',
  marginRight: '2px',
  marginBottom: '-1px',
};

const tabContentStyle: React.CSSProperties = {
  padding: '20px',
  maxHeight: '400px',
  overflow: 'auto',
};

const detailHeaderStyle: React.CSSProperties = {
  padding: '8px',
  textAlign: 'left',
  borderBottom: '1px solid #dee2e6',
  fontSize: '12px',
  fontWeight: 'bold',
};

const detailCellStyle: React.CSSProperties = {
  padding: '6px 8px',
  borderBottom: '1px solid #eee',
};

export default PassHistoryView;
